
const months = { 'Jan': 0, 'Feb': 1, 'Mar': 2, 'Apr': 3, 'May': 4, 'Jun': 5, 'Jul': 6, 'Aug': 7, 'Sep': 8, 'Oct': 9, 'Nov': 10, 'Dec': 11 };

/**
 * Robustly parses various date formats used in the Kit19 API.
 * Supports: 
 * - WCF Date format (/Date(timestamp)/)
 * - ISO formats (Date constructor)
 * - DD-MMM-YYYY or DD-MM-YYYY (with - or / separators)
 * - YYYY-MM-DD or YYYY/MM/DD
 * 
 * @param {string} dateStr - The date string to parse.
 * @returns {Date|null} - A valid Date object or null if parsing fails.
 */
export const parseCustomDate = (dateStr) => {
    if (!dateStr || dateStr === "0" || dateStr === "null" || dateStr === "undefined" || dateStr === "0001-01-01T00:00:00") return null;
    const cleanStr = String(dateStr).trim();

    try {
        // 1. Handle WCF Date format: /Date(1741151000000)/
        if (cleanStr.includes('/Date(')) {
            const timestampStr = cleanStr.match(/\/Date\((-?\d+)\)\//);
            if (timestampStr && timestampStr[1]) {
                const d = new Date(parseInt(timestampStr[1], 10));
                return (isNaN(d.getTime()) || d.getFullYear() < 1900 || d.getFullYear() > 2100) ? null : d;
            }
        }

        // 2. Try formats with '-' or '/' separators manually first to avoid native Date constructor quirks with DD-MM
        const parts = cleanStr.split(' ');
        const datePart = parts[0];
        const dateParts = datePart.includes('-') ? datePart.split('-') : datePart.split('/');

        if (dateParts.length >= 3) {
            let day, month, year;

            if (dateParts[0].length === 4) {
                // YYYY-MM-DD
                year = parseInt(dateParts[0], 10);
                month = parseInt(dateParts[1], 10) - 1;
                day = parseInt(dateParts[2], 10);
            } else if (dateParts[2].length === 4 || dateParts[2].length === 2) {
                // DD-MM-YYYY or DD-MMM-YYYY
                day = parseInt(dateParts[0], 10);
                const monthStr = dateParts[1];
                month = isNaN(monthStr) ? (months[monthStr.substring(0, 3).charAt(0).toUpperCase() + monthStr.substring(1, 3).toLowerCase()] ?? -1) : parseInt(monthStr, 10) - 1;
                const yearStr = dateParts[2];
                year = yearStr.length === 2 ? 2000 + parseInt(yearStr, 10) : parseInt(yearStr, 10);
            }

            // Strict Validation
            if (year < 1900 || year > 2100 || month < 0 || month > 11 || day < 1 || day > 31) {
                return null;
            }

            let hours = 0, minutes = 0, seconds = 0;
            if (parts[1]) {
                const timeParts = parts[1].split(':');
                hours = parseInt(timeParts[0], 10) || 0;
                minutes = parseInt(timeParts[1], 10) || 0;
                seconds = parseInt(timeParts[2], 10) || 0;
                const ampm = (parts[2] || parts[3] || "").toUpperCase();
                if (ampm === 'PM' && hours < 12) hours += 12;
                if (ampm === 'AM' && hours === 12) hours = 0;
            }

            const finalDate = new Date(year, month, day, hours, minutes, seconds);
            return (isNaN(finalDate.getTime()) || finalDate.getFullYear() !== year) ? null : finalDate;
        }

        // 3. Last resort: native Date constructor
        const d = new Date(cleanStr.includes('-') && !cleanStr.includes('T') ? cleanStr.replace(/-/g, '/') : cleanStr);
        if (!isNaN(d.getTime()) && d.getFullYear() >= 1900 && d.getFullYear() <= 2100) return d;

        return null;
    } catch (e) {
        return null;
    }
};

/**
 * Formats a date object or string into a readable string.
 * @param {Date|string} date - The date to format.
 * @param {string} format - The format style ('DD-MMM-YYYY', 'ISO', etc.)
 * @returns {string} - Formatted date string or "-" if invalid.
 */
export const formatCustomDate = (date, format = 'DD-MMM-YYYY') => {
    const d = date instanceof Date ? date : parseCustomDate(date);
    if (!d || isNaN(d.getTime())) return "-";

    const day = String(d.getDate()).padStart(2, '0');
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const month = monthNames[d.getMonth()];
    const year = d.getFullYear();

    if (format === 'DD-MMM-YYYY') {
        return `${day}-${month}-${year}`;
    }
    if (format === 'DD-MMM-YYYY HH:mm') {
        const hrs = String(d.getHours()).padStart(2, '0');
        const min = String(d.getMinutes()).padStart(2, '0');
        return `${day}-${month}-${year} ${hrs}:${min}`;
    }
    return d.toLocaleDateString();
};
